import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';

import {TranslateService} from '@ngx-translate/core';

import {Observable} from 'rxjs';
import {filter, finalize, tap} from 'rxjs/operators';

import {ReportDefinitionService} from './report-definition.service';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../../destroyable.component';
import {ReportDefinition, ReportsFormData} from '../../../models/models.interfaces';
import {FieldType} from '../../form-generator/fields/fields.interfaces';
import {GroupZoneDescriptor, ZoneValues} from '../../form-generator/form-generator.interfaces';
import {
  ConvertToArgumentsOptions,
  GeneratedSimpleFormComponent
} from '../../form-generator/generated-simple-form/generated-simple-form.component';
import {ModalComponent, ModalService} from '../../modal';
import {FileModel, Files, Preview} from '../../utils/files';
import {ProcessInstance} from '../../workflow/workflow-v2.interface';
import {ProfileType} from '../contract/contract.interfaces';
import {EmployeesService} from '../employees.service';
import {ProfileMode} from '../../../app.constants';

@Component({
  selector: 'gp-report-definition',
  templateUrl: 'report-definition-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ReportDefinitionComponent extends Destroyable implements OnChanges {

  private RAW_PREFIX = 'raw:';
  private COMPLEX_TYPES: string[] = [FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED,
    FieldType.LIST, FieldType.TREE, FieldType.PEOPLE_TREE];

  private readonly debugReportTiming: boolean;
  private readonly debugReportForceXmx: boolean;

  @Input()
  public contractId: number;

  @Input()
  public employeeId: number;

  @Input()
  public hiddenValues: Map<any, any>;

  @Input()
  public sendDisplayValues = false;

  @Input()
  public hiddenDisplayValues: Map<string, string>;

  @Input()
  public cliRegIds: Array<number>;

  @Input()
  public selectedReportDefinition: ReportDefinition;

  @Input()
  public types: Array<string>;

  @Input()
  public readOnly: boolean;

  @Input()
  public mySpaceCliRegId: number;

  @Input()
  public profileType: ProfileType;

  @Input()
  public profileMode: ProfileMode;

  @Output()
  public reportDefinitionsLoaded: EventEmitter<ReportsFormData> = new EventEmitter();

  @Output()
  public reportDefinitionListener: EventEmitter<{ reportDefinition: ReportDefinition, status: string }> = new EventEmitter();

  public reportDefinitionsInfo: ReportsFormData;
  public reportDefinitions: Array<ReportDefinition>;
  public reportDefinitionDescriptor: GroupZoneDescriptor;
  public reportDefinitionValues: ZoneValues;
  public reportDefinitionRunning: boolean;
  public reportDefinitionPreview: Preview;

  public isFormDisplayed: boolean = true;
  public displayPreview: boolean = false;

  @ViewChild(GeneratedSimpleFormComponent, {static: true})
  public generatedSimpleForm: GeneratedSimpleFormComponent;

  @ViewChild(ModalComponent, {static: true})
  public modal: ModalComponent;

  public mvelAliases: Array<string>;

  constructor(private reportDefinitionService: ReportDefinitionService,
              private employeesService: EmployeesService,
              private modalService: ModalService,
              private translate: TranslateService,
              private sanitizer: DomSanitizer,
              private ref: ChangeDetectorRef,
              private toastrService: ToastrService) {
    super();
    this.debugReportTiming = localStorage.getItem('gp_debug_report_timing') === 'true';
    this.debugReportForceXmx = localStorage.getItem('gp_debug_report_force_xmx') === 'true';
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.cliRegIds) {
      this.loadReportDefinition();
    } else if (changes.selectedReportDefinition && this.selectedReportDefinition) {
      this.reportDefinitionDescriptor = this.reportDefinitionsInfo.descriptors.get(this.selectedReportDefinition.id);
      this.reportDefinitionValues = this.reportDefinitionsInfo.zonesValues.get(this.selectedReportDefinition.id)[0];
      if (this.contractId) {
        this.mvelAliases = this.reportDefinitionDescriptor.zones
          .filter(z => z.defaultValue && !z.defaultValue.startsWith(this.RAW_PREFIX))
          .map(z => z.defaultValue);
        // for arguments of report contract centric, defaultValue is an mvel alias except if it's prefixed with "raw:"
        this.setDefaultValuesIfNecessary(this.RAW_PREFIX);
      } else {
        this.setDefaultValuesIfNecessary();
      }
      this.modalService.openModalWithId('reportDefinitionModal');
    }
  }

  private setDefaultValuesIfNecessary(prefix: string = ''): void {
    for (const zone of this.reportDefinitionDescriptor.zones) {
      // handle raw default values if no prefix specified or defaultValue starting with prefix when specified
      if (zone.defaultValue && (!prefix || zone.defaultValue.startsWith(prefix))) {
        const defaultValue = zone.defaultValue.replace(new RegExp('^' + prefix), '');
        if (this.COMPLEX_TYPES.includes(zone.type)) {
          try {
            const complexDefaultValue = JSON.parse(defaultValue);
            if (!complexDefaultValue || !complexDefaultValue.value || !complexDefaultValue.transId) {
              throw new Error('bad default value format');
            }
            this.reportDefinitionValues.values[zone.zoneID] = complexDefaultValue;
          } catch (ignored) {
            console.warn('Failed to set default value on ' + zone.type + ' zone, bad format (value/transId required): ' + defaultValue);
          }
        } else {
          this.reportDefinitionValues.values[zone.zoneID].value = defaultValue;
        }
      }
    }
  }

  public reportDefinitionFormLoaded(): void {
    this.isFormDisplayed = this.generatedSimpleForm.isFormDisplayed();
  }

  public resetReportDefinitionData(): void {
    this.reportDefinitionDescriptor = null;
    this.reportDefinitionValues = null;
    this.selectedReportDefinition = null;
    this.reportDefinitionPreview = null;
    this.mvelAliases = null;
  }

  public actionReport(repSettingsModal?: any): void {
    if (!this.generatedSimpleForm.form.valid || !this.selectedReportDefinition) {
      return;
    }
    const args = this.convertFormToArguments() || [];

    if (this.debugReportTiming) {
      args.push({
        name: 'DEBUG_TIMING',
        type: 'BOOLEAN',
        transId: 'DEBUG_TIMING',
        value: true,
        defaultValue: undefined
      });
    }
    if (this.debugReportForceXmx) {
      args.push({
        name: 'DEBUG_FORCE_XMX',
        type: 'LABEL',
        transId: 'DEBUG_FORCE_XMX',
        value: window.prompt('DEBUG_FORCE_XMX'),
        defaultValue: undefined
      });
    }

    switch (this.selectedReportDefinition.reportType) {
      case 'EMPLOYEE_COMMUNICATION':
        this.sendEmployeeCommunication(args, repSettingsModal);
        break;
      case 'EMPLOYEE_ASK_ZONE':
        this.sendEmployeeAskZone(args, repSettingsModal);
        break;
      case 'CLOSING':
      case 'MSS':
        this.generateReport(args, repSettingsModal);
        break;
      default:
        this.generateReport(args, repSettingsModal, true);
    }
  }

  public cancel(): void {
    this.modal.close();
    this.reportDefinitionListener.emit({reportDefinition: this.selectedReportDefinition, status: 'cancelled'});
    this.resetReportDefinitionData();
  }

  public download(): void {
    this.modal.close();
    this.reportDefinitionListener.emit({reportDefinition: this.selectedReportDefinition, status: 'downloaded'});
    this.resetReportDefinitionData();
  }

  private convertFormToArguments() {
    if (!this.sendsDisplayValuesToReportMotor()) {
      return this.generatedSimpleForm.convertToArguments(this.hiddenValues);
    }
    const options: ConvertToArgumentsOptions = {keepEmptyValues: true, hiddenDisplayValues: this.hiddenDisplayValues};
    return this.generatedSimpleForm.convertToArguments(this.hiddenValues, undefined, options);
  }

  private sendsDisplayValuesToReportMotor(): boolean {
    const reportType = this.selectedReportDefinition.reportType;
    return this.sendDisplayValues && reportType !== 'EMPLOYEE_COMMUNICATION' && reportType !== 'EMPLOYEE_ASK_ZONE';
  }

  private loadReportDefinition(): void {
    this.resetReportDefinitionData();
    if (!this.cliRegIds || this.cliRegIds.length <= 0 || !this.types || this.types.length <= 0) {
      this.reportDefinitions = [];
      this.reportDefinitionsLoaded.emit({data: [], descriptors: null, zonesValues: null});
      return;
    }
    this.reportDefinitionService.getReportDefinitionByType(this.cliRegIds, this.types, this.mySpaceCliRegId, this.profileType)
      .subscribe((response: ReportsFormData) => {
        this.reportDefinitionsInfo = response;
        this.reportDefinitions = response.data;
        this.reportDefinitionsLoaded.emit(response);
      });
  }

  private refreshView(): void {
    if (!this.isDestroyed) {
      this.ref.detectChanges();
    }
  }

  private generateReport(args: any, repSettingsModal?: any, immediate?: boolean): void {
    if (immediate) {
      this.generateReportSync(args, repSettingsModal);
    } else {
      this.generateReportAsync(args, repSettingsModal);
    }
  }

  private generateReportAsync(args: any, repSettingsModal?: any): void {
    this.reportDefinitionRunning = true;
    this.selectedReportDefinition.isLoading = true;
    this.reportDefinitionService
      .prepareReport(this.selectedReportDefinition.cliRegId, this.selectedReportDefinition.reportName, args)
      .pipe(finalize(() => {
        this.reportDefinitionRunning = false;
        this.refreshView();
      }))
      .subscribe(() => {
        if (repSettingsModal) {
          repSettingsModal.close();
        }
        this.selectedReportDefinition.isLoading = false;
        this.reportDefinitionListener.emit({reportDefinition: this.selectedReportDefinition, status: 'executed'});
        this.toastrService.success(this.translate.instant('POSTPAYROLL.PREPARE.report_launched', {
          reportName: this.translate.instant(this.selectedReportDefinition.reportName)
        }));
        this.resetReportDefinitionData();
        this.refreshView();
      });
  }

  private generateReportSync(args: any, repSettingsModal?: any): void {
    this.reportDefinitionRunning = true;
    this.reportDefinitionService
      .getReport(this.selectedReportDefinition.cliRegId, this.selectedReportDefinition.reportName, args)
      .pipe(
        filter(Boolean),
        tap((resp: FileModel) => {
          const fileName = resp.fileName;

          if ((navigator as any).msSaveBlob) {
            (navigator as any).msSaveBlob(resp.blob, fileName);
            if (repSettingsModal) {
              repSettingsModal.close();
            }
            this.resetReportDefinitionData();
          } else if (repSettingsModal) {
            const certificateHref = URL.createObjectURL(resp.blob);
            const fileType = resp.type;
            this.displayPreview = fileType === 'application/pdf';
            this.reportDefinitionPreview = {
              iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="500" src="${certificateHref}"
                     name="${fileName}">
             </iframe>
          `),
              href: this.sanitizer.bypassSecurityTrustUrl(certificateHref),
              download: fileName
            };
          } else {
            Files.downloadBlob(resp.blob, fileName, true);
            this.resetReportDefinitionData();
          }
        }),
        finalize(() => {
          this.reportDefinitionRunning = false;
          this.refreshView();
        }))
      .subscribe();
  }

  private sendEmployeeCommunication(args: any, repSettingsModal?: any): void {
    this.reportDefinitionRunning = true;
    const sendOnboarding$ = this.employeesService.sendOnboardingV2(
        this.contractId,
        this.hiddenValues.get('employeeId'),
        args,
        this.selectedReportDefinition.cliRegId,
        this.profileMode || ProfileMode.CLASSIC
      );

    sendOnboarding$.pipe(finalize(() => this.reportDefinitionRunning = false))
      .subscribe({
      next: () => {
        if (repSettingsModal) {
          repSettingsModal.close();
        }
        this.reportDefinitionListener.emit({reportDefinition: this.selectedReportDefinition, status: 'sent'});
        this.toastrService.success(this.translate.instant('ON_BOARDING.sent'));
        this.resetReportDefinitionData();
        this.refreshView();
      },
      error: (err) => {
        this.reportDefinitionRunning = false;
        this.refreshView();
      }
      });
  }

  private sendEmployeeAskZone(args: any, repSettingsModal?: any): void {
    this.reportDefinitionRunning = true;
    this.employeesService.sendAskZone(this.contractId, this.selectedReportDefinition.templateName, args)
      .pipe(finalize(() => this.reportDefinitionRunning = false))
      .subscribe(() => {
        if (repSettingsModal) {
          repSettingsModal.close();
        }
        this.reportDefinitionListener.emit({reportDefinition: this.selectedReportDefinition, status: 'sent'});
        this.resetReportDefinitionData();
        this.refreshView();
      });
  }

  public getActionButton(reportDefinition: ReportDefinition) {
    switch (reportDefinition.reportType) {
      case 'EMPLOYEE_COMMUNICATION':
      case 'EMPLOYEE_ASK_ZONE':
        return this.translate.instant('FORMS.send');
      default:
        return this.translate.instant('FORM_GENERATOR.MODALS.BUTTONS.launch');
    }
  }

  public isFormValid() {
    return this.generatedSimpleForm.form.valid;
  }

}
